<script lang="ts">
  import { APP_NAME, APP_SHORT } from '@ucc/shared';
  import { session } from '$lib/session.svelte';
  import { ApiError, requestForgotPassword } from '$lib/api';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';
  import PasswordField from '$lib/components/PasswordField.svelte';

  let email = $state('');
  let password = $state('');
  let busy = $state(false);
  let formError = $state<string | null>(null);
  let mode = $state<'login' | 'forgot'>('login');
  let forgotMessage = $state<string | null>(null);

  async function submitLogin(e: Event) {
    e.preventDefault();
    formError = null;
    forgotMessage = null;
    busy = true;
    try {
      await session.login(email.trim(), password);
    } catch (err) {
      formError =
        err instanceof ApiError
          ? err.code === 'AUTH_NOT_ACTIVATED'
            ? 'Account not activated yet. Open the invite link from your email first.'
            : err.message
          : err instanceof Error
            ? err.message
            : 'Sign-in failed';
    } finally {
      busy = false;
    }
  }

  async function submitForgot(e: Event) {
    e.preventDefault();
    formError = null;
    forgotMessage = null;
    busy = true;
    try {
      const res = await requestForgotPassword({ email: email.trim() });
      forgotMessage =
        res.message ||
        'If an account exists for that email, a reset link has been sent.';
    } catch (err) {
      formError =
        err instanceof Error ? err.message : 'Could not send reset email. Try again.';
    } finally {
      busy = false;
    }
  }

  function showForgot() {
    mode = 'forgot';
    formError = null;
    forgotMessage = null;
    password = '';
  }

  function showLogin() {
    mode = 'login';
    formError = null;
    forgotMessage = null;
  }
</script>

<div class="login-root">
  <div class="login-panel">
    <header class="brand">
      <div class="mark" aria-hidden="true">
        <img class="mark-logo" src="/logo.svg" width="40" height="40" alt="" />
      </div>
      <div>
        <p class="eyebrow">Secure portal</p>
        <h1>{APP_NAME}</h1>
        <p class="sub">
          {#if mode === 'login'}
            Sign in with your tenant account.
          {:else}
            Enter your account email. If it exists, we will send a reset link.
          {/if}
        </p>
      </div>
    </header>

    <Card>
      {#if mode === 'login'}
        <form class="form" onsubmit={submitLogin}>
          <label class="field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autocomplete="username"
              required
              bind:value={email}
              disabled={busy}
            />
          </label>
          <div class="field">
            <div class="field-head">
              <span>Password</span>
              <button type="button" class="linkish" disabled={busy} onclick={showForgot}>
                Forgot password?
              </button>
            </div>
            <PasswordField
              bind:value={password}
              name="password"
              autocomplete="current-password"
              required
              disabled={busy}
            />
          </div>

          {#if formError}
            <p class="error" role="alert">{formError}</p>
          {/if}

          <Button type="submit" variant="primary" size="lg" disabled={busy} fullWidth>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      {:else}
        <form class="form" onsubmit={submitForgot}>
          <label class="field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autocomplete="username"
              required
              bind:value={email}
              disabled={busy}
            />
          </label>

          {#if formError}
            <p class="error" role="alert">{formError}</p>
          {/if}
          {#if forgotMessage}
            <p class="ok" role="status">{forgotMessage}</p>
          {/if}

          <Button type="submit" variant="primary" size="lg" disabled={busy} fullWidth>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
          <button type="button" class="back" disabled={busy} onclick={showLogin}>
            Back to sign in
          </button>
        </form>
      {/if}
    </Card>
  </div>
</div>

<style>
  .login-root {
    min-height: 100dvh;
    display: grid;
    place-items: center;
    padding: var(--space-6);
    background:
      radial-gradient(1200px 600px at 10% -10%, color-mix(in oklab, var(--brand-500) 28%, transparent), transparent 60%),
      radial-gradient(900px 500px at 100% 0%, color-mix(in oklab, var(--accent-500, #7c5cff) 18%, transparent), transparent 55%),
      var(--bg);
  }

  .login-panel {
    width: min(420px, 100%);
    display: grid;
    gap: var(--space-5);
  }

  .brand {
    display: flex;
    gap: var(--space-4);
    align-items: flex-start;
  }

  .mark {
    width: 52px;
    height: 52px;
    border-radius: var(--radius-lg);
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--bg-elevated, var(--bg)) 92%, var(--brand-500));
    border: 1px solid color-mix(in srgb, var(--border) 65%, var(--brand-400));
    box-shadow: var(--shadow-md);
    flex-shrink: 0;
    overflow: hidden;
    padding: 0.35rem;
  }

  .mark-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  .eyebrow {
    margin: 0;
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-muted);
  }

  h1 {
    margin: 0.15rem 0 0.35rem;
    font-size: var(--text-xl);
    line-height: 1.25;
  }

  .sub {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    line-height: 1.45;
  }

  .form {
    display: grid;
    gap: var(--space-4);
  }

  .field {
    display: grid;
    gap: 0.35rem;
    font-size: var(--text-sm);
  }

  .field span {
    color: var(--text-secondary);
    font-weight: 500;
  }

  .field input {
    border: 1px solid var(--border);
    background: var(--bg-elevated, var(--bg-1, var(--bg)));
    color: var(--text);
    border-radius: var(--radius-md);
    padding: 0.65rem 0.75rem;
    font: inherit;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .field input:focus {
    border-color: var(--brand-500);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--brand-500) 28%, transparent);
  }

  .error {
    margin: 0;
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-md);
    background: color-mix(in oklab, var(--danger, #ef4444) 14%, transparent);
    color: var(--danger-text, #fecaca);
    font-size: var(--text-sm);
  }

  .field-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .field-head > span {
    color: var(--text-muted);
    font-weight: 500;
  }

  .linkish {
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    color: var(--brand-600, #2563eb);
    cursor: pointer;
  }

  .linkish:hover:not(:disabled) {
    text-decoration: underline;
  }

  .linkish:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .ok {
    margin: 0;
    color: var(--success, #059669);
    font-size: var(--text-sm, 0.875rem);
  }

  .back {
    border: none;
    background: none;
    padding: 0.35rem;
    font: inherit;
    font-size: var(--text-sm, 0.875rem);
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
  }

  .back:hover:not(:disabled) {
    color: var(--text, #111);
  }

</style>
